/**
 * @fileoverview Schema for ideated (AI-suggested) storylines.
 *
 * Ideated storylines are temporary suggestions stored on the project
 * until the user explicitly adds or dismisses them. This follows the
 * same pattern as IdeatedCharacterV2 and IdeatedPlot.
 */

import { z } from 'zod';
import { StorylinePriorityEnum, StorylineBeatSchema } from './storyline-planning';

/**
 * A temporary AI-generated storyline suggestion with beats and questions.
 * Stored in `project.ideatedStorylines` until the user acts on it.
 */
export const IdeatedStorylineSchema = z.object({
  tempId: z.string().describe('Temporary unique ID for this ideated storyline.'),
  name: z.string().describe('Short, evocative name for the storyline (2-5 words).'),
  summary: z.string().describe('Brief description of the storyline arc (1-3 sentences).'),
  priority: StorylinePriorityEnum.describe('Storyline priority: A (main plot), B (subplot), C (minor).'),
  beats: z.array(StorylineBeatSchema).describe('Structural beats for this storyline.'),
  storyQuestions: z.array(z.object({
    question: z.string().describe('A dramatic question raised by this storyline.'),
  })).describe('Story questions that create dramatic tension.'),
  source: z.enum(['ideation', 'finder']).describe('Source of this storyline suggestion.'),
});

export type IdeatedStoryline = z.infer<typeof IdeatedStorylineSchema>;
